import * as React from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Background,
  BackgroundVariant,
  Node,
  Edge,
  MarkerType,
  Connection as RFConnection,
} from "@xyflow/react";
import { Plus, Trash2, Maximize, Minimize } from "lucide-react";
import ELK from "elkjs/lib/elk.bundled.js";
import "@xyflow/react/dist/style.css";
import { useNavigate } from "react-router-dom";

import { CustomNode } from "@/components/flow/nodes";
import { CustomEdge } from "@/components/flow/custom-edge";
import { Input } from "@/components/ui/input";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ProjectContext, ProjectContextType } from "@/context/project";
import {
  nodes as nodeConfigs,
  templateTypeMapping,
  nodeTypes,
} from "@/config/nodes";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NodeConfigTable, NodeConfigApp } from "@/components/node-config-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AppEdge {
  source: string;
  target: string;
  data?: Record<string, unknown>;
}

export type FlowWidgetMode = "view-edit" | "view-only" | "create";

export interface FlowWidgetProps {
  name?: string;
  domainName?: string;
  apps: Record<
    string,
    {
      id: string;
      name: string;
      template: string;
      config: Record<string, unknown>;
    }
  >;
  edges: AppEdge[];
  mode?: FlowWidgetMode;
  hideCreateControls?: boolean;
  onChange?: (state: {
    apps: Record<string, unknown>;
    edges: AppEdge[];
  }) => void;
  onSave?: () => void;
}

interface NodeData extends Record<string, unknown> {
  type: string;
  key: string;
  name: string;
}

type CustomNodeType = Node<NodeData>;

const customNodeTypes = nodeTypes.reduce(
  (acc, type) => {
    acc[type] = CustomNode;
    return acc;
  },
  {} as Record<string, typeof CustomNode>,
);
const customEdgeTypes = {
  custom: CustomEdge,
};

const elk = new ELK({
  workerUrl: undefined, // Disable web worker
});

const getLayoutedElements = async (nodes: CustomNodeType[], edges: Edge[]) => {
  const graph = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      "elk.spacing.nodeNode": "50",
      "elk.layered.spacing.baseValue": "50",
      "elk.padding": "[top=10, left=10, bottom=10, right=10]",
    },
    children: nodes.map((node) => ({
      id: node.id,
      width: 240,
      height: 120,
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  const layoutedGraph = await elk.layout(graph);

  const layoutedNodes = nodes.map((node) => {
    const layoutedNode = layoutedGraph.children?.find((n) => n.id === node.id);
    return {
      ...node,
      position: {
        x: (layoutedNode?.x || 0) - 50,
        y: (layoutedNode?.y || 0) - 25,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

export const FlowWidget: React.FC<FlowWidgetProps & { className?: string }> = ({
  name: initialName = "",
  domainName: initialDomainName = "",
  apps = {},
  edges: connectionEdges = [],
  mode = "view-edit",
  hideCreateControls = false,
  className,
  onChange,
  onSave: _onSave,
}) => {
  const { projectsMap, setProjectsMap } =
    React.useContext<ProjectContextType>(ProjectContext);
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState<CustomNodeType>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [projectName, setProjectName] = React.useState(initialName);
  const [domainName, setDomainName] = React.useState(initialDomainName);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [localApps, setLocalApps] = React.useState(apps);
  const [stagingApps, setStagingApps] = React.useState<Record<string, unknown>>(
    {},
  );
  const [localEdges, setLocalEdges] = React.useState(connectionEdges);
  const [isMainPopoverOpen, setIsMainPopoverOpen] = React.useState(false);
  const [editingNodeId, setEditingNodeId] = React.useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const isViewOnly = mode === "view-only";
  const isCreateMode = mode === "create";

  const getStrokeColor = () =>
    document.documentElement.classList.contains("dark") ? "#E5E7EB" : "#1A192B";

  const [strokeColor, setStrokeColor] = React.useState(getStrokeColor());

  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setStrokeColor(getStrokeColor());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Sync props to state only when they meaningfully change
  // Track last seen prop values to only sync when they change from above
  const lastAppsPropRef = React.useRef(JSON.stringify(apps));
  const lastEdgesPropRef = React.useRef(JSON.stringify(connectionEdges));
  
  // combined state ref for onChange detection
  const lastAppsInternalRef = React.useRef(JSON.stringify(apps));
  const lastEdgesInternalRef = React.useRef(JSON.stringify(connectionEdges));

  const onChangeRef = React.useRef(onChange);

  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  React.useEffect(() => {
    const appsStr = JSON.stringify(apps);
    if (appsStr !== lastAppsPropRef.current) {
      setLocalApps(apps);
      lastAppsPropRef.current = appsStr;
      lastAppsInternalRef.current = appsStr;
    }
  }, [apps]);

  React.useEffect(() => {
    const edgesStr = JSON.stringify(connectionEdges);
    if (edgesStr !== lastEdgesPropRef.current) {
      setLocalEdges(connectionEdges || []);
      lastEdgesPropRef.current = edgesStr;
      lastEdgesInternalRef.current = edgesStr;
    }
  }, [connectionEdges]);

  // We want to trigger onChange when LOCAL state changes, 
  // but we must be careful not to create loops.
  const isInternalUpdate = React.useRef(false);

  React.useEffect(() => {
    if (isInternalUpdate.current) {
      const appsStr = JSON.stringify(localApps);
      const edgesStr = JSON.stringify(localEdges);
      
      onChangeRef.current?.({ apps: localApps, edges: localEdges });
      
      // Update internal tracking refs so we don't think we need to trigger CHANGE again
      lastAppsInternalRef.current = appsStr;
      lastEdgesInternalRef.current = edgesStr;
      
      // CRITICAL: Also update the Prop refs to match our current state.
      // If we don't, the next render will see AppsProp (old) !== internalState (new) 
      // and overwrite our edits with the old props!
      lastAppsPropRef.current = appsStr;
      lastEdgesPropRef.current = edgesStr;
      
      isInternalUpdate.current = false;
    }
  }, [localApps, localEdges]);

  // Synchronize ReactFlow edges with localEdges
  React.useEffect(() => {
    const rfEdges = (localEdges || []).map((edge, idx) => ({
      id: `e-${edge.source}-${edge.target}-${idx}`,
      source: edge.source,
      target: edge.target,
      data: edge.data || {},
      type: "custom",
      animated: false,
      style: {
        stroke: getStrokeColor(),
        strokeWidth: 1,
      },
      markerEnd: {
        type: MarkerType.Arrow,
        width: 20,
        height: 20,
        color: getStrokeColor(),
      },
    }));
    setEdges(rfEdges);
  }, [localEdges, strokeColor, setEdges]);

  // Synchronize ReactFlow nodes with localApps
  React.useEffect(() => {
    const initialNodes =
      Object.values(localApps)?.map((app) => {
        const appType = templateTypeMapping[app.template] || "web";
        return {
          id: app.id,
          type: appType,
          position: { x: 0, y: 0 },
          data: {
            id: app.id,
            type: appType,
            key: app.id,
            name: app.name,
            template: app.template,
            config: app.config || {},
            onEdit: !isViewOnly
              ? (id: string) => setEditingNodeId(id)
              : undefined,
            completedMilestones: [],
          },
        };
      }) || [];

    setNodes((prevNodes) => {
      // Check if we need to layout (initial load or node count changed)
      const needsLayout = prevNodes.length === 0 || prevNodes.length !== initialNodes.length;

      if (needsLayout) {
        // Run layouting
        const nodeIds = new Set(initialNodes.map((n) => n.id));
        const layoutInputEdges = (localEdges || [])
          .filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target))
          .map((edge, idx) => ({
            id: `e-${edge.source}-${edge.target}-${idx}`,
            source: edge.source,
            target: edge.target,
          })) as Edge[];

        getLayoutedElements(initialNodes as CustomNodeType[], layoutInputEdges).then(
          ({ nodes: layoutedNodes }) => {
            setNodes(layoutedNodes);
          },
        );
        return prevNodes; // Will be overwritten by getLayoutedElements
      } else {
        // Just update data, preserve positions
        return initialNodes.map((inNode) => {
          const prev = prevNodes.find((p) => p.id === inNode.id);
          return {
            ...inNode,
            position: prev?.position || inNode.position,
          };
        });
      }
    });
  }, [localApps, isViewOnly]);

  const handleCreateProject = async () => {
    if (!projectName.trim() || !domainName.trim()) {
      setError("Project Name and Domain Name are required.");
      return;
    }
    setError(null);
    setCreating(true);

    const payloadApps: Record<
      string,
      { name: string; template: string; config: Record<string, unknown> }
    > = {};
    Object.values(localApps).forEach((app) => {
      payloadApps[app.id] = {
        name: app.name,
        template: app.template,
        config: app.config,
      };
    });

    const payload = {
      name: projectName,
      domainName,
      apps: payloadApps,
      edges: localEdges,
    };

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/project`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          credentials: "include",
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data?.data?.id) {
          setProjectsMap({
            ...projectsMap,
            [data?.data?.id]: data?.data,
          });
          navigate(`/projects/${data?.data?.id}`);
        }
      } else {
        const errorData = await response.json();
        setError(errorData?.message || "Failed to create project");
      }
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setCreating(false);
    }
  };

  const handleAddNode = (type: string, templateValue: string) => {
    const id = `${type}-${crypto.randomUUID().slice(0, 3)}`;
    const config: Record<string, unknown> = {};
    const nodeConfig = nodeConfigs[type as keyof typeof nodeConfigs];

    if (nodeConfig?.checkboxOptions) {
      Object.entries(nodeConfig.checkboxOptions).forEach(
        ([key, opt]: [string, unknown]) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          config[key] = (opt as any).defaultValue;
        },
      );
    }

    const newApp = {
      id,
      name: id,
      template: templateValue,
      config,
    };
    setStagingApps((prev) => ({
      ...prev,
      [id]: newApp,
    }));
  };

  const updateApp = (id: string, updates: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setStagingApps((prev: Record<string, any>) => ({
      ...prev,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [id]: { ...(prev[id] as any), ...(updates as any) },
    }));
  };

  const removeApp = (id: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setStagingApps((prev: Record<string, any>) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleCommitToCanvas = () => {
    // Validate auth-service nodes have OAuth providers selected
    const authServiceApps = Object.values(stagingApps).filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (app: any) => {
        const template = app.template;
        const type = templateTypeMapping[template];
        return type === "auth-service";
      }
    );

    for (const authApp of authServiceApps) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const providers = (authApp as any).config?.providers as string[] | undefined;
      if (!providers || providers.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setError(`The auth-service node "${(authApp as any).name}" requires at least one OAuth provider to be selected.`);
        return;
      }
      
      // Check if auth-service is connected to a database
      // Since this is commit to canvas, edges might not be established yet for new nodes relative to existing ones if handled in a specific order,
      // but in this flow, edges are drawn AFTER adding to canvas usually. 
      // However, check if we can validate topology here? 
      // Actually, edges are added via ReactFlow connection, usually on the canvas. 
      // CommitToCanvas puts nodes ONTO the canvas. Connections happen later.
      // So ensuring connections here is premature for CommitToCanvas (user validates via UI later).
      // BUT for Auth Provider config inside the node, it IS appropriate.
    }

    // Validate custom-repo nodes have PORT provided
    const githubRepoApps = Object.values(stagingApps).filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (app: any) => {
        const template = app.template;
        const type = templateTypeMapping[template];
        return type === "custom-repo";
      },
    );

    for (const repoApp of githubRepoApps) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const port = (repoApp as any).config?.port as string | undefined;
      if (!port || port.trim() === "") {
        setError(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          `The custom-repo node "${(repoApp as any).name}" requires a PORT to be provided.`,
        );
        return;
      }
      if (isNaN(Number(port))) {
        setError(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          `The PORT for custom-repo node "${(repoApp as any).name}" must be a valid number.`,
        );
        return;
      }
    }

    // Validate custom-url nodes have PORT and GitHub URL provided
    const githubUrlApps = Object.values(stagingApps).filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (app: any) => {
        const template = app.template;
        const type = templateTypeMapping[template];
        return type === "custom-url";
      },
    );

    for (const urlApp of githubUrlApps) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const port = (urlApp as any).config?.port as string | undefined;
      if (!port || port.trim() === "") {
        setError(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          `The custom-url node "${(urlApp as any).name}" requires a PORT to be provided.`,
        );
        return;
      }
      if (isNaN(Number(port))) {
        setError(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          `The PORT for custom-url node "${(urlApp as any).name}" must be a valid number.`,
        );
        return;
      }
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const githubUrl = (urlApp as any).config?.githubUrl as string | undefined;
      if (!githubUrl || githubUrl.trim() === "") {
        setError(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          `The custom-url node "${(urlApp as any).name}" requires a GitHub URL to be provided.`,
        );
        return;
      }
    }

    // Validate apps with db/auth enabled? 
    // Similar to above, topology validation usually happens on "Save" or "Next Step", not "Add Node to Canvas".
    // So distinct from NewProjectMilestone which is a wizard.
    // FlowWidget allows drawing.
    // But we should ensure constraints on Save.

    isInternalUpdate.current = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setLocalApps((prev: Record<string, any>) => ({
      ...prev,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...(stagingApps as any),
    }));
    setStagingApps({});
    setIsMainPopoverOpen(false);
    setError(null);
  };

  const handleCancel = () => {
    setStagingApps({});
    setIsMainPopoverOpen(false);
  };

  const handleUpdateLocalApp = (id: string, updates: unknown) => {
    isInternalUpdate.current = true;
    setLocalApps((prev) => ({
      ...prev,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [id]: { ...prev[id], ...(updates as any) },
    }));
  };

  const handleRemoveLocalApp = (id: string) => {
    isInternalUpdate.current = true;
    setLocalApps((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (editingNodeId === id) setEditingNodeId(null);
  };

  const onEdgesDelete = React.useCallback(
    (deletedEdges: Edge[]) => {
      isInternalUpdate.current = true;
      setLocalEdges((prev) =>
        prev.filter(
          (le) =>
            !deletedEdges.some(
              (de) => de.source === le.source && de.target === le.target,
            ),
        ),
      );
    },
    [setLocalEdges],
  );

  const onConnect = React.useCallback(
    (params: RFConnection) => {
      if (params.source && params.target) {
        isInternalUpdate.current = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setLocalEdges((eds: any[]) => [
          ...eds,
          {
            source: params.source!,
            target: params.target!,
            data: {
              deploymentUrl: false,
              allowCors: false,
            },
          },
        ]);
      }
    },
    [setLocalEdges],
  );

  const NodeTypeBadge = ({
    type,
    config,
    isDisabled,
  }: {
    type: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    config: any;
    isDisabled: boolean;
  }) => {
    const [open, setOpen] = React.useState(false);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            className={`group flex items-center gap-2.5 rounded-lg border border-border bg-muted/50 px-3 py-1.5 transition-all duration-200 ${
              isDisabled
                ? "cursor-not-allowed opacity-30 grayscale"
                : "cursor-pointer hover:border-primary/30 hover:bg-primary/5 hover:shadow-md active:scale-95"
            }`}
          >
            <span className="text-blue-500 drop-shadow-sm">{config?.icon}</span>
            <span className="text-xs font-bold tracking-tight text-muted-foreground lowercase group-hover:text-primary">
              {type}
            </span>
            <Plus
              className={`h-3 w-3 transition-colors ${isDisabled ? "text-muted" : "text-muted-foreground group-hover:text-primary"}`}
            />
          </div>
        </PopoverTrigger>
        {!isDisabled && (
          <PopoverContent
            className="w-64 overflow-hidden rounded-xl border border-border p-0 shadow-xl"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <div className="border-b bg-muted/50 px-3 py-2">
              <p className="text-[10px] font-bold tracking-wider text-muted-foreground uppercase">
                Select Template
              </p>
            </div>
            <div className="grid gap-0.5 p-1">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {config?.templates?.map((t: any) => (
                <Button
                  key={t.value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={`h-9 justify-start gap-3 px-3 text-xs font-semibold ${t.disabled ? "opacity-50 grayscale-0" : "hover:bg-primary/5 hover:text-primary"}`}
                  disabled={t.disabled}
                  onClick={() => {
                    handleAddNode(type, t.value);
                    setOpen(false);
                  }}
                >
                  <span className="flex h-5 w-5 items-center justify-center opacity-80">
                    {t.icon}
                  </span>
                  <div className="flex flex-col items-start leading-none">
                    <span>{t.label}</span>
                    {t.disabled && (
                      <span className="text-[9px] font-normal text-muted-foreground">
                        Coming Soon
                      </span>
                    )}
                  </div>
                </Button>
              ))}
              {(!config?.templates || config.templates.length === 0) && (
                <p className="p-3 text-center text-[10px] text-muted-foreground italic">
                  No templates available
                </p>
              )}
            </div>
          </PopoverContent>
        )}
      </Popover>
    );
  };

  return (
    <div className={cn(
      "relative h-full w-full",
      isFullscreen && "fixed inset-0 z-50 bg-background",
      className
    )}>
      {/* Fullscreen Toggle Button */}
      <div className="pointer-events-auto absolute top-6 right-4 z-20">
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "flex h-8 w-8 items-center justify-center rounded-md border bg-background/80 shadow-sm backdrop-blur-md transition-all hover:bg-muted active:scale-95",
          )}
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? (
            <Minimize className="h-4 w-4" />
          ) : (
            <Maximize className="h-4 w-4" />
          )}
        </button>
      </div>

      {!isViewOnly && (
        <div className="pointer-events-auto absolute top-6 left-4 z-20">
          <Popover
            modal={false}
            open={isMainPopoverOpen}
            onOpenChange={setIsMainPopoverOpen}
          >
            <PopoverTrigger asChild>
              <button
                className={cn(
                  buttonVariants({ variant: "ghost" }),
                  "flex h-8 items-center justify-center gap-2 rounded-md border bg-background/80 px-4 shadow-sm backdrop-blur-md transition-all hover:bg-muted active:scale-95",
                )}
                title="Add Nodes"
              >
                <Plus className="h-4 w-4" />
                <span className="text-sm text-foreground">Add Node</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[80vw] border-none bg-transparent p-0 shadow-2xl"
              align="start"
              sideOffset={8}
            >
              <div className="flex flex-col rounded-xl border bg-background/95 p-6 backdrop-blur-md">
                <div className="pb-4">
                  <div className="flex flex-wrap gap-3 px-1 pt-1">
                    {nodeTypes.map((type) => {
                      const config = nodeConfigs[type];
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const isDisabled = (config as any)?.disabled;

                      return (
                        <NodeTypeBadge
                          key={type}
                          type={type}
                          config={config}
                          isDisabled={!!isDisabled}
                        />
                      );
                    })}
                  </div>
                </div>

                {Object.keys(stagingApps).length > 0 && (
                  <ScrollArea className="h-[40vh] w-full pr-4">
                    <NodeConfigTable
                      apps={
                        Object.values(stagingApps).map((app) => ({
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          ...(app as any),
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          type: templateTypeMapping[(app as any).template] || "web",
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          config: (app as any).config || {},
                        })) as NodeConfigApp[]
                      }
                      onUpdateApp={updateApp}
                      onRemoveApp={removeApp}
                    />
                  </ScrollArea>
                )}

                <div className="mt-6 flex flex-col gap-4">
                  {error && (
                    <p className="text-right text-xs font-medium text-destructive">
                      {error}
                    </p>
                  )}
                  <div className="flex justify-end gap-3">
                    <Button
                      variant="ghost"
                      type="button"
                      className="h-10 rounded-xl px-6 text-xs font-bold text-muted-foreground hover:bg-accent"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      className="h-10 rounded-xl px-8 text-xs font-bold transition-all active:scale-95"
                      onClick={handleCommitToCanvas}
                      disabled={Object.keys(stagingApps).length === 0}
                    >
                      Add to Canvas
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {isCreateMode && !hideCreateControls && (
        <div className="pointer-events-auto absolute top-4 right-4 z-20">
          <div className="flex min-h-[48px] flex-col justify-center gap-1 rounded-xl border bg-background/80 p-2 shadow-2xl backdrop-blur-md">
            <div className="grid grid-cols-3 gap-1">
              <Input
                placeholder="Project Name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="h-8 border-none bg-transparent text-sm focus-visible:ring-1 focus-visible:ring-primary/20"
              />
              <Input
                placeholder="Domain Name"
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
                className="h-8 border-none bg-transparent text-sm focus-visible:ring-1 focus-visible:ring-primary/20"
              />
              <Button
                type="button"
                onClick={handleCreateProject}
                disabled={creating}
                className="h-8 px-4 text-xs font-semibold transition-all active:scale-95"
              >
                {creating ? "Creating..." : "Create Project"}
              </Button>
            </div>
            {error && (
              <p className="px-2 py-1 text-[10px] font-medium text-destructive">
                {error}
              </p>
            )}
          </div>
        </div>
      )}
      <div className="h-full w-full">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={customNodeTypes}
          edgeTypes={customEdgeTypes}
          fitView
          fitViewOptions={{
            padding: 0.5,
          }}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgesDelete={!isViewOnly ? onEdgesDelete : undefined}
          onConnect={!isViewOnly ? onConnect : undefined}
          nodesDraggable={!isViewOnly}
          nodesConnectable={!isViewOnly}
          panOnDrag={true}
          zoomOnScroll={true}
          zoomOnPinch={true}
          zoomOnDoubleClick={true}
          defaultEdgeOptions={{
            type: "custom",
            style: {
              stroke: strokeColor,
              strokeWidth: 1,
            },
            markerEnd: {
              type: MarkerType.Arrow,
              width: 20,
              height: 20,
              color: strokeColor,
            },
          }}
        >
          <Background
            color="#888"
            gap={10}
            size={0.5}
            variant={BackgroundVariant.Cross}
          />
        </ReactFlow>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}

      {!isViewOnly && (
        <Dialog
          open={!!editingNodeId}
          onOpenChange={(open) => !open && setEditingNodeId(null)}
        >
          <DialogContent className="max-w-[960px] overflow-hidden border-none p-0 shadow-2xl sm:max-w-[960px] [&>button:last-child]:hidden">
            <div className="bg-background/95 backdrop-blur-md p-6">
              <DialogHeader className="mb-6">
                <DialogTitle className="text-xl font-bold text-foreground">
                  Edit Node
                </DialogTitle>
              </DialogHeader>

                {editingNodeId && localApps[editingNodeId] && (
                  <NodeConfigTable
                    apps={[
                      {
                        ...localApps[editingNodeId],
                        type:
                          templateTypeMapping[
                            localApps[editingNodeId].template
                          ] || "web",
                        config: localApps[editingNodeId].config || {},
                      } as NodeConfigApp,
                    ]}
                    onUpdateApp={handleUpdateLocalApp}
                    onRemoveApp={handleRemoveLocalApp}
                    hideRemoveIcon={true}
                  />
                )}

              <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    editingNodeId && handleRemoveLocalApp(editingNodeId)
                  }
                  className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                >
                  <Trash2 />
                  Delete Node
                </Button>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setEditingNodeId(null)}
                    className=""
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      if (editingNodeId && localApps[editingNodeId]) {
                        const app = localApps[editingNodeId];
                        const type = templateTypeMapping[app.template];
                        if (type === "auth-service") {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const providers = (app.config as any)?.providers;
                          if (!providers || providers.length === 0) {
                            setError(
                              "Auth service nodes require at least one OAuth provider.",
                            );
                            return;
                          }
                        }
                        if (type === "github-repo") {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const port = (app.config as any)?.port;
                          if (!port || port.trim() === "") {
                            setError(
                              "Github repo nodes require a PORT to be provided.",
                            );
                            return;
                          }
                          if (isNaN(Number(port))) {
                            setError("The PORT must be a valid number.");
                            return;
                          }
                        }
                      }
                      setError(null);
                      setEditingNodeId(null);
                    }}
                    className="transition-all active:scale-95"
                  >
                    Apply Changes
                  </Button>
                </div>
              </div>
              {error && (
                <p className="mt-2 text-right text-xs font-semibold text-destructive">
                  {error}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
